import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePack, useUpdatePack } from '../hooks/usePacks';
import { useCustomers, useCreateCustomer } from '../hooks/useCustomers';
import type { Customer } from '../lib/api';
import Button from '../components/Button';
import Input from '../components/Input';
import SearchBar from '../components/SearchBar';
import Card from '../components/Card';

export default function EditPack() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: pack, isLoading: packLoading } = usePack(id || '');
  const updatePack = useUpdatePack();
  const createCustomer = useCreateCustomer();
  const { data: customersData } = useCustomers({ limit: 1000 });

  const [formData, setFormData] = useState({
    pack_id: '',
    batch_id: '',
    bp_manufacturer: '',
    cells_in_series: '',
    cells_in_parallel: '',
    total_cells: '',
    customer: '',
    customer_location: '',
    dispatch_dt: '',
    activation_date: '',
    sale_date: '',
    bms: '',
    status: '',
    pack_status_reason: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerList, setShowCustomerList] = useState(false);
  const customerDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pack) {
      setFormData({
        pack_id: pack.pack_id,
        batch_id: pack.batch_id || '',
        bp_manufacturer: pack.bp_manufacturer || '',
        cells_in_series: pack.cells_in_series?.toString() || '',
        cells_in_parallel: pack.cells_in_parallel?.toString() || '',
        total_cells: pack.total_cells?.toString() || '',
        customer: pack.customer || '',
        customer_location: pack.customer_location || '',
        dispatch_dt: pack.dispatch_dt ? pack.dispatch_dt.split('T')[0] : '',
        activation_date: pack.activation_date ? pack.activation_date.split('T')[0] : '',
        sale_date: pack.sale_date ? pack.sale_date.split('T')[0] : '',
        bms: pack.bms || '',
        status: pack.status || '',
        pack_status_reason: pack.pack_status_reason || '',
      });
    }
  }, [pack]);

  const allCustomers = customersData?.items || [];

  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return allCustomers;
    const searchLower = customerSearch.toLowerCase();
    return allCustomers.filter(
      (customer: Customer) =>
        customer.customer?.toLowerCase().includes(searchLower) ||
        customer.customer_location?.toLowerCase().includes(searchLower)
    );
  }, [allCustomers, customerSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setShowCustomerList(false);
      }
    };

    if (showCustomerList) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomerList]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setErrors({});

    try {
      // If customer name is provided and doesn't exist in the list, create it
      if (formData.customer.trim()) {
        const customerExists = allCustomers.some(
          (c: Customer) => c.customer.toLowerCase() === formData.customer.trim().toLowerCase()
        );

        if (!customerExists) {
          await createCustomer.mutateAsync({
            customer: formData.customer.trim(),
            customer_location: formData.customer_location.trim() || undefined,
          });
        }
      }

      await updatePack.mutateAsync({
        id,
        data: {
          batch_id: formData.batch_id.trim() || undefined,
          bp_manufacturer: formData.bp_manufacturer.trim() || undefined,
          cells_in_series: formData.cells_in_series ? parseInt(formData.cells_in_series, 10) : undefined,
          cells_in_parallel: formData.cells_in_parallel ? parseInt(formData.cells_in_parallel, 10) : undefined,
          total_cells: formData.total_cells ? parseInt(formData.total_cells, 10) : undefined,
          customer: formData.customer.trim() || undefined,
          customer_location: formData.customer_location.trim() || undefined,
          dispatch_dt: formData.dispatch_dt ? `${formData.dispatch_dt}T00:00:00.000Z` : undefined,
          activation_date: formData.activation_date ? `${formData.activation_date}T00:00:00.000Z` : undefined,
          sale_date: formData.sale_date ? `${formData.sale_date}T00:00:00.000Z` : undefined,
          bms: formData.bms.trim() || undefined,
          status: formData.status.trim() || undefined,
          pack_status_reason: formData.pack_status_reason.trim() || undefined,
        },
      });
      navigate(`/packs/${id}`);
    } catch (error) {
      setErrors({ submit: 'Failed to update pack. Please try again.' });
    }
  };

  if (packLoading) {
    return <div className="p-8 text-center text-gray-500">Loading pack data...</div>;
  }

  if (!pack) {
    return <div className="p-8 text-center text-red-500">Pack not found</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
        <div className="flex items-center gap-3 w-full md:w-auto">
          <button
            onClick={() => navigate(`/packs/${id}`)}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors bg-white shadow-sm border border-gray-200"
            aria-label="Back to pack details"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl md:text-3xl font-extrabold text-gray-900 leading-tight break-all">
              Edit Pack: {id}
            </h1>
            <p className="text-xs md:text-sm font-medium text-gray-500 uppercase tracking-wider mt-0.5">
              Update battery pack information
            </p>
          </div>
        </div>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="Pack ID"
            value={formData.pack_id}
            disabled
            className="bg-gray-50 bg-opacity-50"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Batch ID"
              value={formData.batch_id}
              onChange={(e) => setFormData({ ...formData, batch_id: e.target.value })}
              placeholder="e.g., BATCH-001"
            />

            <Input
              label="Manufacturer"
              value={formData.bp_manufacturer}
              onChange={(e) => setFormData({ ...formData, bp_manufacturer: e.target.value })}
              placeholder="Battery pack manufacturer"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Cells in Series"
              type="number"
              value={formData.cells_in_series}
              onChange={(e) => setFormData({ ...formData, cells_in_series: e.target.value })}
              placeholder="e.g., 7"
            />

            <Input
              label="Cells in Parallel"
              type="number"
              value={formData.cells_in_parallel}
              onChange={(e) => setFormData({ ...formData, cells_in_parallel: e.target.value })}
              placeholder="e.g., 2"
            />

            <Input
              label="Total Cells"
              type="number"
              value={formData.total_cells}
              onChange={(e) => setFormData({ ...formData, total_cells: e.target.value })}
              placeholder="e.g., 14"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-base font-semibold mb-2 text-gray-700">
                Customer
              </label>
              <div className="relative" ref={customerDropdownRef}>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.customer}
                    onChange={(e) => {
                      setFormData({ ...formData, customer: e.target.value });
                      setShowCustomerList(true);
                      setCustomerSearch(e.target.value);
                    }}
                    onFocus={() => setShowCustomerList(true)}
                    placeholder="Type or select customer..."
                    className="touch-target w-full pl-4 pr-10 py-3 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white hover:border-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCustomerList(!showCustomerList)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 touch-target min-w-[32px] min-h-[32px] flex items-center justify-center"
                  >
                    <svg
                      className={`w-5 h-5 transition-transform ${showCustomerList ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>

                {showCustomerList && (
                  <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-xl shadow-lg max-h-80 overflow-hidden">
                    <div className="p-3 border-b border-gray-200">
                      <SearchBar
                        placeholder="Search customers..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        onClear={() => setCustomerSearch('')}
                      />
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {filteredCustomers.length === 0 && !customerSearch.trim() ? (
                        <div className="px-4 py-3 text-sm text-gray-500">No customers found</div>
                      ) : filteredCustomers.length === 0 && customerSearch.trim() ? (
                        <div className="px-4 py-3 text-sm text-gray-500">
                          No customers found. "{customerSearch}" will be created as a new customer.
                        </div>
                      ) : (
                        filteredCustomers.map((customer: Customer) => (
                          <button
                            key={customer.customer}
                            type="button"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                customer: customer.customer,
                                customer_location: customer.customer_location || formData.customer_location,
                              });
                              setShowCustomerList(false);
                              setCustomerSearch('');
                            }}
                            className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${formData.customer === customer.customer
                              ? 'bg-blue-50 text-blue-700 font-medium'
                              : 'text-gray-700'
                              }`}
                          >
                            <div className="font-medium">{customer.customer}</div>
                            {customer.customer_location && (
                              <div className="text-xs text-gray-500 mt-1">{customer.customer_location}</div>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <Input
              label="Customer Location"
              value={formData.customer_location}
              onChange={(e) => setFormData({ ...formData, customer_location: e.target.value })}
              placeholder="Location"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Dispatch Date"
              type="date"
              value={formData.dispatch_dt}
              onChange={(e) => setFormData({ ...formData, dispatch_dt: e.target.value })}
            />

            <Input
              label="BMS"
              value={formData.bms}
              onChange={(e) => setFormData({ ...formData, bms: e.target.value })}
              placeholder="BMS identifier"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Activation Date"
              type="date"
              value={formData.activation_date}
              onChange={(e) => setFormData({ ...formData, activation_date: e.target.value })}
            />

            <Input
              label="Sale Date"
              type="date"
              value={formData.sale_date}
              onChange={(e) => setFormData({ ...formData, sale_date: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-base font-semibold mb-2 text-gray-700">Status</label>
              <select
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="Idle">Idle</option>
                <option value="Deployed">Deployed</option>
                <option value="Decommissioned">Decommissioned</option>
                <option value="Scrapped">Scrapped</option>
              </select>
            </div>

            <Input
              label="Status Reason"
              value={formData.pack_status_reason}
              onChange={(e) => setFormData({ ...formData, pack_status_reason: e.target.value })}
              placeholder="Reason for status change"
            />
          </div>

          {errors.submit && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4">
              <p className="text-sm text-red-700">{errors.submit}</p>
            </div>
          )}

          <div className="flex gap-4">
            <Button
              type="submit"
              variant="primary"
              disabled={updatePack.isPending}
            >
              {updatePack.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(`/packs/${id}`)}
              disabled={updatePack.isPending}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
